import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Link, useLocation } from 'react-router-dom'
import { getProjects } from '../api/projects'
import { generateAgentsBlock, generateClaudeHooksSetup, generateSkill } from '../api/skill'
import AgentHarnessAutoInstall from '../components/setup/AgentHarnessAutoInstall'
import {
  harnessInstallSummary,
  SKILL_HARNESS_PATHS,
  SKILL_HARNESSES,
} from '../components/setup/harnessMeta'
import SetupGuideShell from '../components/setup/SetupGuideShell'
import SetupStepper from '../components/setup/SetupStepper'
import { useAuth } from '../hooks/useAuth'
import type { ProjectResponse } from '../types/api'
import { isGlobalAdmin } from '../utils/rbac'

const selectClassName =
  'w-full rounded-lg border border-harbor-border bg-harbor-surface-raised px-3 py-2 text-harbor-text focus:border-transparent focus:outline-none focus:ring-2 focus:ring-emerald-500'

export default function ProjectSetupPage() {
  const location = useLocation()
  const { user } = useAuth()
  const isAdmin = isGlobalAdmin(user?.role)
  const [projects, setProjects] = useState<ProjectResponse[]>([])
  const [isLoadingProjects, setIsLoadingProjects] = useState(true)
  const [selectedProject, setSelectedProject] = useState('')
  const [harness, setHarness] = useState('cursor')
  const [skillContent, setSkillContent] = useState('')
  const [agentsContent, setAgentsContent] = useState('')
  const [hookScript, setHookScript] = useState('')
  const [hookSettings, setHookSettings] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const [activeStep, setActiveStep] = useState(0)

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const data = await getProjects()
        setProjects(data)
        if (data.length > 0 && !selectedProject) {
          setSelectedProject(data[0].name)
        }
      } catch {
        toast.error('Failed to load projects')
      } finally {
        setIsLoadingProjects(false)
      }
    }
    fetchProjects()
  }, [selectedProject])

  const handleGenerate = async () => {
    if (!selectedProject) return
    setIsGenerating(true)
    try {
      const [skill, agents] = await Promise.all([
        generateSkill(selectedProject),
        generateAgentsBlock(selectedProject),
      ])
      setSkillContent(skill.skillMarkdown)
      setAgentsContent(agents.agentsBlock)
      if (harness === 'claude-code') {
        const hooks = await generateClaudeHooksSetup(selectedProject)
        setHookScript(hooks.scriptContent)
        setHookSettings(JSON.stringify(hooks.settingsFragment, null, 2))
      } else {
        setHookScript('')
        setHookSettings('')
      }
      toast.success('Install files generated')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to generate files')
    } finally {
      setIsGenerating(false)
    }
  }

  const copyText = async (key: string, text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedKey(key)
      toast.success(`${label} copied to clipboard`)
      setTimeout(() => setCopiedKey(null), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }

  const skillPath = SKILL_HARNESS_PATHS[harness] ?? SKILL_HARNESS_PATHS.generic
  const generated = skillContent.length > 0

  const copyButtonClassName =
    'rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-harbor-text transition-colors hover:bg-emerald-700'

  if (isLoadingProjects) {
    return (
      <SetupGuideShell
        pathname={location.pathname}
        title="Project Setup"
        description="Install the skill file and AGENTS.md block for a project, then index the repo for code-aware answers — one agent prompt does both."
      >
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500" />
        </div>
      </SetupGuideShell>
    )
  }

  if (projects.length === 0) {
    return (
      <SetupGuideShell
        pathname={location.pathname}
        title="Project Setup"
        description="Install the skill file and AGENTS.md block for a project, then index the repo for code-aware answers — one agent prompt does both."
      >
        <div className="rounded-xl border border-harbor-border bg-harbor-surface p-12 text-center">
          <div className="text-5xl mb-4">📁</div>
          <h2 className="text-lg font-semibold text-harbor-text mb-2">No projects yet</h2>
          <p className="text-sm text-harbor-text-secondary mb-4">
            Create a project first, then return here to install the skill and AGENTS.md block.
          </p>
          {isAdmin ? (
            <Link
              to="/projects?create=1"
              className="inline-block px-4 py-2 bg-emerald-600 text-harbor-text text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Create a project
            </Link>
          ) : (
            <p className="text-sm text-harbor-text-secondary">
              Ask an administrator to create a project or grant you access.
            </p>
          )}
        </div>
      </SetupGuideShell>
    )
  }

  return (
    <SetupGuideShell
      pathname={location.pathname}
      title="Project Setup"
      description="Install the skill file and AGENTS.md block for a project, then index the repo for code-aware answers — one agent prompt does both."
    >
      <SetupStepper
        activeStep={activeStep}
        onStepChange={setActiveStep}
        steps={[
          {
            title: 'Choose project & harness',
            children: (
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label
                      htmlFor="project-setup-project"
                      className="mb-1 block text-sm font-medium text-harbor-text-secondary"
                    >
                      Project
                    </label>
                    <select
                      id="project-setup-project"
                      value={selectedProject}
                      onChange={(e) => setSelectedProject(e.target.value)}
                      className={selectClassName}
                    >
                      {projects.map((project) => (
                        <option key={project.id} value={project.name}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="project-setup-harness"
                      className="mb-1 block text-sm font-medium text-harbor-text-secondary"
                    >
                      Editor / harness
                    </label>
                    <select
                      id="project-setup-harness"
                      value={harness}
                      onChange={(e) => setHarness(e.target.value)}
                      className={selectClassName}
                    >
                      {SKILL_HARNESSES.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="text-sm text-harbor-text-secondary">
                  Auto-install will set up {harnessInstallSummary(harness)} for{' '}
                  <span className="font-medium text-harbor-text">
                    {selectedProject || '<project>'}
                  </span>{' '}
                  and index the repo for code-aware answers.
                </p>
              </div>
            ),
          },
          {
            title: 'Auto-install via agent (recommended)',
            children: (
              <AgentHarnessAutoInstall
                projectName={selectedProject}
                harness={harness}
                onHarnessChange={setHarness}
              />
            ),
          },
          {
            title: 'Manual install (copy files)',
            children: (
              <div className="space-y-6">
                <div className="space-y-4">
                  <p className="text-sm text-harbor-text-secondary">
                    Generate the install files for{' '}
                    <span className="font-medium text-harbor-text">
                      {selectedProject || '<project>'}
                    </span>{' '}
                    and copy each one into your repo. Regenerate whenever you change the project or
                    harness in step 1.
                  </p>
                  <button
                    onClick={handleGenerate}
                    disabled={isGenerating || !selectedProject}
                    className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-harbor-text transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isGenerating ? 'Generating...' : 'Generate Install Files'}
                  </button>
                </div>
                {!generated ? (
                  <p>
                    No files yet — click “Generate Install Files”. The generated content will appear
                    here ready to copy.
                  </p>
                ) : (
                  <>
                    <div className="space-y-3">
                      <h3 className="font-medium text-harbor-text">
                        Skill file — <code className="text-emerald-400">{skillPath}</code>
                      </h3>
                      <div className="max-h-96 overflow-auto rounded-lg bg-harbor-surface-raised p-4">
                        <pre className="whitespace-pre-wrap font-mono text-sm text-harbor-text-secondary">
                          {skillContent}
                        </pre>
                      </div>
                      <button
                        onClick={() => copyText('skill', skillContent, 'Skill')}
                        className={copyButtonClassName}
                      >
                        {copiedKey === 'skill' ? 'Copied!' : 'Copy Skill'}
                      </button>
                    </div>
                    <div className="space-y-3">
                      <h3 className="font-medium text-harbor-text">
                        AGENTS.md block — <code className="text-emerald-400">AGENTS.md</code>
                      </h3>
                      <p>
                        Merge this block into your repo&apos;s <code>AGENTS.md</code> (create it if
                        it doesn&apos;t exist), keeping the managed block markers intact.
                      </p>
                      <div className="max-h-96 overflow-auto rounded-lg bg-harbor-surface-raised p-4">
                        <pre className="whitespace-pre-wrap font-mono text-sm text-harbor-text-secondary">
                          {agentsContent}
                        </pre>
                      </div>
                      <button
                        onClick={() => copyText('agents', agentsContent, 'AGENTS.md block')}
                        className={copyButtonClassName}
                      >
                        {copiedKey === 'agents' ? 'Copied!' : 'Copy AGENTS.md Block'}
                      </button>
                    </div>
                    {harness === 'claude-code' && (
                      <>
                        <div className="space-y-3">
                          <h3 className="font-medium text-harbor-text">
                            CLAUDE.md block — <code className="text-emerald-400">CLAUDE.md</code>
                          </h3>
                          <p>
                            Same block as AGENTS.md — merge it into your repo&apos;s{' '}
                            <code>CLAUDE.md</code> the same way.
                          </p>
                          <button
                            onClick={() => copyText('claude', agentsContent, 'CLAUDE.md block')}
                            className={copyButtonClassName}
                          >
                            {copiedKey === 'claude' ? 'Copied!' : 'Copy CLAUDE.md Block'}
                          </button>
                        </div>
                        <div className="space-y-3">
                          <h3 className="font-medium text-harbor-text">
                            Stop hook script —{' '}
                            <code className="text-emerald-400">
                              .claude/hooks/context-harbor-stop-check.mjs
                            </code>
                          </h3>
                          <div className="max-h-96 overflow-auto rounded-lg bg-harbor-surface-raised p-4">
                            <pre className="whitespace-pre-wrap font-mono text-sm text-harbor-text-secondary">
                              {hookScript}
                            </pre>
                          </div>
                          <button
                            onClick={() => copyText('hook', hookScript, 'Hook script')}
                            className={copyButtonClassName}
                          >
                            {copiedKey === 'hook' ? 'Copied!' : 'Copy Hook Script'}
                          </button>
                        </div>
                        <div className="space-y-3">
                          <h3 className="font-medium text-harbor-text">
                            Settings fragment —{' '}
                            <code className="text-emerald-400">.claude/settings.json</code>
                          </h3>
                          <p>
                            Merge this fragment into your project&apos;s{' '}
                            <code>.claude/settings.json</code> alongside any hooks you already have
                            — don&apos;t overwrite the file.
                          </p>
                          <div className="max-h-96 overflow-auto rounded-lg bg-harbor-surface-raised p-4">
                            <pre className="whitespace-pre-wrap font-mono text-sm text-harbor-text-secondary">
                              {hookSettings}
                            </pre>
                          </div>
                          <button
                            onClick={() => copyText('settings', hookSettings, 'Settings fragment')}
                            className={copyButtonClassName}
                          >
                            {copiedKey === 'settings' ? 'Copied!' : 'Copy Settings'}
                          </button>
                        </div>
                      </>
                    )}
                    <div className="space-y-3">
                      <h3 className="font-medium text-harbor-text">
                        Code graph — <code className="text-emerald-400">agent-driven indexing</code>
                      </h3>
                      <ol className="list-decimal space-y-2 pl-5">
                        <li>
                          In your agent (with the <code>context-harbor</code> MCP server
                          configured), call{' '}
                          <code className="text-emerald-400">setup_code_graph</code> with project
                          name <code className="text-emerald-400">{selectedProject}</code>.
                        </li>
                        <li>
                          Write the returned <code>ch-code-graph-setup.sh</code> (sh) and{' '}
                          <code>ch-code-graph-setup.ps1</code> (PowerShell) into the repo checkout.
                        </li>
                        <li>
                          Run the platform script against the repo directory (auth reuses the
                          harness MCP key — <code className="text-emerald-400">CH_MCP_TOKEN</code>{' '}
                          env overrides when set):{' '}
                          <code className="text-emerald-400">sh ./ch-code-graph-setup.sh</code> on
                          macOS/Linux/Git Bash, or{' '}
                          <code className="text-emerald-400">
                            powershell -ExecutionPolicy Bypass -File .\ch-code-graph-setup.ps1
                          </code>{' '}
                          on Windows PowerShell. Ask which branch to index first and pass it as{' '}
                          <code className="text-emerald-400">CH_BRANCH</code> when non-interactive.
                        </li>
                        <li>
                          Verify with <code className="text-emerald-400">get_code_brief</code> until
                          the index reports READY, then delete the temp files (the setup script file
                          plus the .chcode bundle if one remains). Afterwards{' '}
                          <code className="text-emerald-400">code_context</code> /{' '}
                          <code className="text-emerald-400">code_impact</code> answer from the
                          fresh index.
                        </li>
                      </ol>
                    </div>
                    <ol className="list-decimal space-y-2 pl-5">
                      <li>
                        Write the skill content to{' '}
                        <code className="text-emerald-400">{skillPath}</code> (overwrite).
                      </li>
                      <li>
                        Merge the block into <code className="text-emerald-400">AGENTS.md</code>
                        {harness === 'claude-code' ? (
                          <>
                            {' '}
                            and <code className="text-emerald-400">CLAUDE.md</code>, and install the
                            Stop hook script + settings fragment
                          </>
                        ) : (
                          ' — auto-install covers CLAUDE.md/hooks only for Claude Code'
                        )}
                        , then index the repo with the code-graph steps above.
                      </li>
                    </ol>
                  </>
                )}
              </div>
            ),
          },
        ]}
      />
    </SetupGuideShell>
  )
}
